import { box, type Box } from '../geometry/box';
import type { Size } from '../geometry/framing';
import { coverCrop } from '../camera/layer';
import { nextKeptTime, outputLengthWithin, range, sourceToOutputTime, type PlayRange, type TimeRange } from '../time/ranges';
import type { AssetId } from './sequence';
import { NO_MOTION, parseTextMotion, textMotionAt, type TextMotion, type TextMotionFrame } from './text-motion';
import { cleanText, DEFAULT_TEXT_STYLE, layoutText, parseTextStyle, TEXT_REFERENCE_PX, textAspect, type MeasureText, type TextBlock, type TextStyle } from './text-layer';

/**
 * Things put on top of the edit: a video (B-roll, picture in picture), a
 * picture (logo, sticker), a sound (music, voice-over) or words (a title).
 *
 * A layer is pinned to the moment of the edit it starts at (`start`, row
 * time) and lasts until `end`, so it stays with what it illustrates when more
 * is cut around it. What it shows plays on without jumps: parts cut from the
 * edit underneath shorten the layer instead of skipping through its video or
 * song.
 */
export type MediaLayerKind = 'video' | 'image' | 'audio';
export type LayerKind = MediaLayerKind | 'text';
export const LAYER_KINDS: readonly LayerKind[] = ['video', 'image', 'audio', 'text'];

/** Where a picture layer sits: its centre (0..1 of the frame) and its width as a share of the frame's. */
export interface LayerPlacement {
  readonly x: number;
  readonly y: number;
  readonly size: number;
}

interface LayerBase {
  readonly id: string;
  /** Row seconds, `[start, end)`. */
  readonly start: number;
  readonly end: number;
  readonly placement: LayerPlacement;
  readonly opacity: number;
  /** Eases in and out instead of cutting in. */
  readonly fade: boolean;
  /** Left out of the preview and the export, kept in the edit. */
  readonly hidden: boolean;
  /** Can't be moved or resized by accident, on the timeline or the frame. */
  readonly locked: boolean;
}

/** A layer that shows or plays an imported file. */
export interface MediaLayer extends LayerBase {
  readonly kind: MediaLayerKind;
  readonly assetId: AssetId;
  /** Seconds into the asset where it starts playing (video and sound). */
  readonly offset: number;
  /** Covers the whole frame (cropped to fill it) instead of sitting at `placement`. */
  readonly fill: boolean;
  /** 0..1; a video layer starts silent so the main sound carries on. */
  readonly volume: number;
  /** Starts over (from `offset`) when the asset runs out before the layer ends. */
  readonly loop: boolean;
  /** Sound gets quieter while someone speaks in the edit. */
  readonly duck: boolean;
}

/** Words set on the frame; `placement.size` is the width of the text block. */
export interface TextLayer extends LayerBase {
  readonly kind: 'text';
  readonly text: string;
  readonly style: TextStyle;
  /** How the words come on and go off (instead of `fade`, which words don't use). */
  readonly motion: TextMotion;
}

export type Layer = MediaLayer | TextLayer;

export const LAYER_SIZE = { min: 0.06, max: 1 } as const;
export const MIN_LAYER_SEC = 0.2;
/** A picture added without a length of its own shows this long. */
export const DEFAULT_IMAGE_SEC = 5;
/** New words stay on screen this long. */
export const DEFAULT_TEXT_SEC = 4;

const PICTURE_FADE_SEC = 0.35;
const SOUND_FADE_IN_SEC = 1;
const SOUND_FADE_OUT_SEC = 1.5;

/** Ducking: −10 dB under speech, quick to duck, slower to come back so it doesn't pump between words. */
export const DUCK = { level: 0.32, attack: 0.18, release: 0.45 } as const;

const DEFAULT_PLACEMENT: Readonly<Record<LayerKind, LayerPlacement>> = {
  video: { x: 0.74, y: 0.26, size: 0.4 },
  image: { x: 0.5, y: 0.5, size: 0.36 },
  audio: { x: 0.5, y: 0.5, size: 0.4 },
  text: { x: 0.5, y: 0.5, size: 0.6 },
};

const clamp = (value: number, min: number, max: number) => (max < min ? min : Math.min(max, Math.max(min, value)));

export const isMediaLayer = (layer: Layer): layer is MediaLayer => layer.kind !== 'text';
export const isTextLayer = (layer: Layer): layer is TextLayer => layer.kind === 'text';
/** The imported file a layer uses (words use none). */
export const layerAssetId = (layer: Layer): AssetId | null => (isMediaLayer(layer) ? layer.assetId : null);
export const isVisual = (layer: Pick<Layer, 'kind'>): boolean => layer.kind !== 'audio';
export const hasSound = (layer: Layer): layer is MediaLayer => (layer.kind === 'video' || layer.kind === 'audio') && layer.volume > 0;
/** Covers the whole frame (only pictures and videos can). */
export const fillsFrame = (layer: Layer): boolean => isMediaLayer(layer) && layer.fill;

/**
 * A new layer at row time `at`: as long as the asset (a picture: a few
 * seconds), never past the end of the edit. Music starts at the beginning
 * when it is added at the very end, where it would have no room.
 */
export function createLayer(kind: MediaLayerKind, assetId: AssetId, id: string, options: { readonly at: number; readonly rowDuration: number; readonly assetDuration: number }): MediaLayer {
  const length = kind === 'image' ? DEFAULT_IMAGE_SEC : options.assetDuration;
  const { start, end } = spanAt(options.at, length, options.rowDuration);
  return {
    id,
    kind,
    assetId,
    start,
    end,
    offset: 0,
    fill: false,
    placement: DEFAULT_PLACEMENT[kind],
    opacity: 1,
    volume: kind === 'audio' ? 0.8 : 0,
    fade: kind === 'audio',
    loop: false,
    duck: kind === 'audio',
    hidden: false,
    locked: false,
  };
}

/**
 * New words at row time `at`, centred, for a few seconds. They cut in rather
 * than fade, like pictures: a fade would leave them invisible on the very frame
 * they were added on, which is the frame on screen.
 */
export function createTextLayer(
  id: string,
  options: { readonly at: number; readonly rowDuration: number; readonly text: string; readonly style?: TextStyle; readonly motion?: TextMotion; readonly size?: number },
): TextLayer {
  const { start, end } = spanAt(options.at, DEFAULT_TEXT_SEC, options.rowDuration);
  return {
    id,
    kind: 'text',
    text: cleanText(options.text),
    style: options.style ?? DEFAULT_TEXT_STYLE,
    motion: options.motion ?? NO_MOTION,
    start,
    end,
    placement: options.size === undefined ? DEFAULT_PLACEMENT.text : placeLayer({ ...DEFAULT_PLACEMENT.text, size: options.size }),
    opacity: 1,
    fade: false,
    hidden: false,
    locked: false,
  };
}

/** Where something `length` long added at `at` goes: from there if it has room, otherwise from the start. */
function spanAt(at: number, length: number, rowDuration: number): { start: number; end: number } {
  const roomy = rowDuration - at >= Math.min(length, 1);
  const start = clamp(roomy ? at : 0, 0, Math.max(0, rowDuration - MIN_LAYER_SEC));
  return { start, end: Math.min(rowDuration, start + Math.max(MIN_LAYER_SEC, length)) };
}

/** New words are set at this share of the frame's shorter side: a caption-sized title. */
export const TEXT_FONT_SHARE = 0.075;

/** Width share a text block needs for its letters to be `fontShare` of the frame's shorter side. */
export function textSizeFor(block: Pick<TextBlock, 'width'>, output: Size, fontShare = TEXT_FONT_SHARE): number {
  const font = fontShare * Math.min(output.width, output.height);
  return clamp(((block.width / TEXT_REFERENCE_PX) * font) / Math.max(1, output.width), LAYER_SIZE.min, LAYER_SIZE.max);
}

/**
 * Where words sit after their text or style changed. The letters keep their
 * size, as in any editor: the block grows or shrinks around them (up to the
 * frame's width, after which they get smaller to fit), and words sent to an
 * anchor stay at it.
 */
export function refitText(layer: TextLayer, next: { readonly text: string; readonly style: TextStyle }, output: Size, measure: MeasureText): LayerPlacement {
  const before = layoutText(layer.text, layer.style, measure);
  const after = layoutText(next.text, next.style, measure);
  const size = clamp(layer.placement.size * (after.width / before.width), LAYER_SIZE.min, LAYER_SIZE.max);
  const anchor = anchorOf(layer.placement, textAspect(before), output);
  return anchor ? anchorPlacement(anchor, size, textAspect(after), output) : placeLayer({ ...layer.placement, size });
}

/** Distance a copy is set off from its original, so it is seen as a second layer. */
const DUPLICATE_SHIFT = 0.04;

/** A copy of `layer` (unlocked and shown), a little down and right of it when it sits on the frame. */
export function duplicateLayer(layer: Layer, id: string): Layer {
  const free = isVisual(layer) && !fillsFrame(layer);
  const placement = free ? placeLayer({ ...layer.placement, x: layer.placement.x + DUPLICATE_SHIFT, y: layer.placement.y + DUPLICATE_SHIFT }) : layer.placement;
  return { ...layer, id, placement, hidden: false, locked: false };
}

/** Longest the layer can run from `offset` without looping (a picture or words have no end). */
export const playableLength = (layer: Layer, assetDuration: number): number =>
  !isMediaLayer(layer) || layer.kind === 'image' || layer.loop ? Infinity : Math.max(0, assetDuration - layer.offset);

/** Moves a layer along the row, keeping its length and keeping it inside the edit. */
export function moveLayer<T extends Layer>(layer: T, start: number, rowDuration: number): T {
  const length = layer.end - layer.start;
  const next = clamp(start, 0, Math.max(0, rowDuration - length));
  return { ...layer, start: next, end: next + length };
}

/**
 * Drags one edge. The start edge of a video or sound also moves where in the
 * asset it starts, like trimming a clip: the content stays put on the row.
 */
export function resizeLayer<T extends Layer>(layer: T, edge: 'start' | 'end', time: number, rowDuration: number, assetDuration: number): T {
  if (edge === 'end') {
    const max = Math.min(rowDuration, layer.start + playableLength(layer, assetDuration));
    return { ...layer, end: clamp(time, layer.start + MIN_LAYER_SEC, max) };
  }
  if (!isMediaLayer(layer) || layer.kind === 'image') return { ...layer, start: clamp(time, 0, layer.end - MIN_LAYER_SEC) };
  const earliest = Math.max(0, layer.start - layer.offset);
  const start = clamp(time, earliest, layer.end - MIN_LAYER_SEC);
  return { ...layer, start, offset: Math.max(0, layer.offset + (start - layer.start)) };
}

/** Keeps a picture layer's centre on the frame and its size in bounds. */
export const placeLayer = (placement: LayerPlacement): LayerPlacement => ({
  x: clamp(placement.x, 0, 1),
  y: clamp(placement.y, 0, 1),
  size: clamp(placement.size, LAYER_SIZE.min, LAYER_SIZE.max),
});

/** The nine places a layer can be sent to in one tap: corners, edges' middles and the centre. */
export type LayerAnchor = `${'top' | 'middle' | 'bottom'}-${'left' | 'center' | 'right'}`;
export const LAYER_ANCHORS: readonly LayerAnchor[] = [
  'top-left',
  'top-center',
  'top-right',
  'middle-left',
  'middle-center',
  'middle-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];
/** Margin kept from the frame's edges, as a share of its shorter side. */
const ANCHOR_MARGIN = 0.05;

/**
 * Centre of a layer `size` wide (share of the frame's width) and `aspect`
 * (width / height) sent to `anchor`, `ANCHOR_MARGIN` in from the edges.
 */
export function anchorPlacement(anchor: LayerAnchor, size: number, aspect: number, output: Size): LayerPlacement {
  const [vertical, horizontal] = anchor.split('-') as ['top' | 'middle' | 'bottom', 'left' | 'center' | 'right'];
  const margin = (ANCHOR_MARGIN * Math.min(output.width, output.height)) / output.width;
  const marginY = (ANCHOR_MARGIN * Math.min(output.width, output.height)) / output.height;
  const halfW = size / 2;
  const halfH = (size * output.width) / (aspect > 0 ? aspect : 1) / output.height / 2;
  const x = horizontal === 'left' ? margin + halfW : horizontal === 'right' ? 1 - margin - halfW : 0.5;
  const y = vertical === 'top' ? marginY + halfH : vertical === 'bottom' ? 1 - marginY - halfH : 0.5;
  // Something wider than the frame between its margins is centred instead.
  return placeLayer({ x: halfW * 2 > 1 - margin * 2 ? 0.5 : x, y: halfH * 2 > 1 - marginY * 2 ? 0.5 : y, size });
}

/** The anchor a placement sits at, if any (within a hair). */
export function anchorOf(placement: LayerPlacement, aspect: number, output: Size): LayerAnchor | null {
  return LAYER_ANCHORS.find((anchor) => {
    const at = anchorPlacement(anchor, placement.size, aspect, output);
    return Math.abs(at.x - placement.x) < 0.005 && Math.abs(at.y - placement.y) < 0.005;
  }) ?? null;
}

/** Moves a layer one step up (towards the front, +1) or down the stack; the same list when it can't go. */
export function restack(layers: readonly Layer[], id: string, direction: 1 | -1): readonly Layer[] {
  const index = layers.findIndex((layer) => layer.id === id);
  return moveInStack(layers, id, index + direction);
}

/** Puts a layer at `index` of the stack (0 = bottom). */
export function moveInStack(layers: readonly Layer[], id: string, index: number): readonly Layer[] {
  const from = layers.findIndex((layer) => layer.id === id);
  if (from === -1 || index < 0 || index >= layers.length || index === from) return layers;
  const next = [...layers];
  const [moved] = next.splice(from, 1);
  next.splice(index, 0, moved!);
  return next;
}

/**
 * A layer as a project saved by an earlier version kept it: whatever it lacks
 * (shown, unlocked; a text style) is filled in.
 */
export function normalizeLayer(layer: Layer): Layer {
  const base = { ...layer, hidden: layer.hidden === true, locked: layer.locked === true };
  if (base.kind === 'text') {
    // Words used to fade with the pictures' switch; their motion says it now.
    return { ...base, text: cleanText(typeof base.text === 'string' ? base.text : ''), style: parseTextStyle(base.style), motion: parseTextMotion(base.motion, base.fade), fade: false };
  }
  return base;
}

// ——— Playing a layer on the edited timeline ———

/** Output seconds a layer plays, `[start, end)`. */
export interface LayerSpan {
  readonly start: number;
  readonly end: number;
}

/**
 * Where the layer plays in the finished video: from the first kept moment at
 * or after its start, for as long as the edit keeps of its row stretch. Null
 * when everything under it was cut.
 */
export function layerSpan(layer: Pick<Layer, 'start' | 'end'>, keep: readonly PlayRange[]): LayerSpan | null {
  const first = nextKeptTime(keep, layer.start);
  if (first === null || first >= layer.end) return null;
  const start = sourceToOutputTime(keep, first);
  const length = outputLengthWithin(keep, range(layer.start, layer.end));
  return start === null || length <= 0 ? null : { start, end: start + length };
}

/** A layer ready to draw or mix: what the preview and the exporter both work from. */
export interface ResolvedLayer {
  readonly layer: Layer;
  readonly span: LayerSpan;
  /** Seconds of video or sound in the asset (a picture: Infinity). */
  readonly assetDuration: number;
  /** Width / height of its picture (1 for sound). */
  readonly aspect: number;
}

/** Width / height of a text layer's block, measured where there is a canvas to measure with. */
export type TextAspect = (layer: TextLayer) => number;

/** Shown layers with something left to show after the edit; hidden ones and those whose file is gone are left out. */
export function resolveLayers(
  layers: readonly Layer[],
  keep: readonly PlayRange[],
  assetOf: (id: AssetId) => { readonly durationSec: number; readonly aspect: number } | undefined,
  textAspect: TextAspect = () => 3,
): ResolvedLayer[] {
  const resolved: ResolvedLayer[] = [];
  for (const layer of layers) {
    if (layer.hidden) continue;
    const span = layerSpan(layer, keep);
    if (!span) continue;
    if (!isMediaLayer(layer)) {
      const aspect = textAspect(layer);
      resolved.push({ layer, span, assetDuration: Infinity, aspect: aspect > 0 && Number.isFinite(aspect) ? aspect : 1 });
      continue;
    }
    const asset = assetOf(layer.assetId);
    if (!asset) continue;
    resolved.push({ layer, span, assetDuration: layer.kind === 'image' ? Infinity : asset.durationSec, aspect: asset.aspect > 0 ? asset.aspect : 1 });
  }
  return resolved;
}

/** Shows forever once on (a picture or words, or anything looping). */
const endless = (layer: Layer): boolean => !isMediaLayer(layer) || layer.kind === 'image' || layer.loop;

/** Asset seconds playing at output time `t`, or null when the layer isn't on then (or its asset ran out). */
export function contentTime(resolved: ResolvedLayer, t: number): number | null {
  const { layer, span, assetDuration } = resolved;
  if (t < span.start || t >= span.end) return null;
  const elapsed = t - span.start;
  if (!isMediaLayer(layer) || layer.kind === 'image') return elapsed;
  const length = assetDuration - layer.offset;
  if (!(length > 0)) return null;
  if (layer.loop) return layer.offset + (elapsed % length);
  return elapsed < length ? layer.offset + elapsed : null;
}

/**
 * Asset seconds a layer plays at row time `t`: what its bar on the timeline
 * shows there. Null outside the layer, in a part the edit cuts (the layer
 * waits there) and after its file has run out.
 */
export function contentAtRow(resolved: ResolvedLayer, keep: readonly PlayRange[], t: number): number | null {
  if (t < resolved.layer.start || t >= resolved.layer.end) return null;
  const output = sourceToOutputTime(keep, t);
  return output === null ? null : contentTime(resolved, output);
}

/** How far the layer is through its fade-in/out at output time `t` (0..1). */
export function fadeAt(resolved: ResolvedLayer, t: number): number {
  const { layer, span } = resolved;
  if (t < span.start || t >= span.end) return 0;
  // Words move by their own motion (see `textMotionOf`).
  if (!layer.fade || !isMediaLayer(layer)) return 1;
  const third = (span.end - span.start) / 3;
  const fadeIn = Math.min(third, layer.kind === 'audio' ? SOUND_FADE_IN_SEC : PICTURE_FADE_SEC);
  const fadeOut = Math.min(third, layer.kind === 'audio' ? SOUND_FADE_OUT_SEC : PICTURE_FADE_SEC);
  const end = !isMediaLayer(layer) || endless(layer) ? span.end : Math.min(span.end, span.start + (resolved.assetDuration - layer.offset));
  const rise = fadeIn > 0 ? (t - span.start) / fadeIn : 1;
  const fall = fadeOut > 0 ? (end - t) / fadeOut : 1;
  return smooth(clamp(Math.min(rise, fall), 0, 1));
}

/** Ease in and out, so fades and ducking start and land softly. */
const smooth = (x: number): number => x * x * (3 - 2 * x);

/** Index of the first range that ends after `t`. */
function firstEndingAfter(ranges: readonly TimeRange[], t: number): number {
  let lo = 0;
  let hi = ranges.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (ranges[mid]!.end <= t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Gain a ducked sound gets at output time `t` given where speech is (output
 * time, sorted): `level` under speech, easing down just before it starts and
 * back up after it ends.
 */
export function duckGain(speech: readonly TimeRange[], t: number, profile: { readonly level: number; readonly attack: number; readonly release: number } = DUCK): number {
  const index = firstEndingAfter(speech, t);
  const next = speech[index];
  const previous = speech[index - 1];
  let depth = 0;
  if (next && t >= next.start) depth = 1;
  else {
    if (next && profile.attack > 0) depth = Math.max(depth, 1 - (next.start - t) / profile.attack);
    if (previous && profile.release > 0) depth = Math.max(depth, 1 - (t - previous.end) / profile.release);
  }
  return 1 - (1 - profile.level) * smooth(clamp(depth, 0, 1));
}

/** Loudness of a layer's sound at output time `t` (0 when it is silent then). */
export function soundGainAt(resolved: ResolvedLayer, t: number, speech: readonly TimeRange[]): number {
  const { layer } = resolved;
  if (!hasSound(layer) || contentTime(resolved, t) === null) return 0;
  return layer.volume * fadeAt(resolved, t) * (layer.duck ? duckGain(speech, t) : 1);
}

/** Opacity of a picture layer at output time `t` (0 when it isn't showing). */
export function opacityAt(resolved: ResolvedLayer, t: number): number {
  if (!isVisual(resolved.layer) || contentTime(resolved, t) === null) return 0;
  return resolved.layer.opacity * fadeAt(resolved, t);
}

/** How the words of a text layer move at output time `t`. */
export const textMotionOf = (resolved: ResolvedLayer & { readonly layer: TextLayer }, t: number): TextMotionFrame =>
  textMotionAt(resolved.layer.motion, resolved.layer.text, t - resolved.span.start, resolved.span.end - resolved.span.start);

/** Where a picture layer lands on the output frame (output pixels). */
/** What places a layer on the frame (words never fill it). */
export interface LayerFrameSpot {
  readonly fill?: boolean;
  readonly placement: LayerPlacement;
}

export function layerRect(layer: LayerFrameSpot, output: Size, aspect: number): Box {
  if (layer.fill) return box(0, 0, output.width, output.height);
  const width = layer.placement.size * output.width;
  const height = width / (aspect > 0 ? aspect : 1);
  return box(layer.placement.x * output.width - width / 2, layer.placement.y * output.height - height / 2, width, height);
}

/** The part of the layer's picture (its pixels) shown in `rect`: all of it, or the middle when it fills a frame of another shape. */
export function layerCrop(layer: Pick<LayerFrameSpot, 'fill'>, source: Size, rect: Box): Box {
  return layer.fill ? coverCrop(source, rect.w / rect.h) : box(0, 0, source.width, source.height);
}

/** What a picture layer draws at output time `t`, or null when it isn't showing. */
export function layerFrameAt(resolved: ResolvedLayer, t: number, output: Size): { readonly time: number; readonly opacity: number; readonly rect: Box } | null {
  const time = contentTime(resolved, t);
  const opacity = opacityAt(resolved, t);
  if (time === null || opacity <= 0.001) return null;
  return { time, opacity, rect: layerRect(resolved.layer, output, resolved.aspect) };
}

/** Top-most picture layer under a point (output pixels) at output time `t`. */
export function layerAtPoint(resolved: readonly ResolvedLayer[], t: number, output: Size, point: { readonly x: number; readonly y: number }): ResolvedLayer | null {
  for (let i = resolved.length - 1; i >= 0; i--) {
    const candidate = resolved[i]!;
    if (!isVisual(candidate.layer) || contentTime(candidate, t) === null) continue;
    const rect = layerRect(candidate.layer, output, candidate.aspect);
    if (point.x >= rect.x && point.x <= rect.x + rect.w && point.y >= rect.y && point.y <= rect.y + rect.h) return candidate;
  }
  return null;
}
